/* LylaLoo Custom Design Fee sync.
   A $10 fee line (product "custom-design-fee") rides along with any photo-upload order of the 4 custom products.
   The cart itself is the source of truth: after every cart change this reconciles
   "main item with an uploaded photo"  <->  "fee line with property _fee_for = main line key" (same quantity).
   Orders without an uploaded photo never get a fee. No checkout/cart-logic of the theme is modified. */
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const FEE_VARIANT_ID = 67724757762091;
const FEE_HANDLE = 'custom-design-fee';
const FEE_HANDLES = new Set(['stained-glass-acrylic', 'stained-glass-framed', 'pet-challenge-coin', 'custom-pet-name-tumbler', 'custom-christmas-wreath-dog-tumbler', 'custom-christmas-ornament-dog-tumbler']);
const PHOTO_PROPERTY = 'Custom photo';
const OWN = '__llDesignFee';
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

let running = false;
let queued = null; // merged options for a run requested while one is in flight
let timer = null;

const needsFee = (item) =>
  FEE_HANDLES.has(item.handle) && item.properties && String(item.properties[PHOTO_PROPERTY] || '').trim() !== '' &&
  String(item.properties['Design style'] || '').trim() !== 'Realistic photo crop'; // UV: no fee for Realistic photo crop (JR, Oct 30 2026)
const isFee = (item) => Number(item.variant_id) === FEE_VARIANT_ID;

async function getCart() {
  const r = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!r.ok) throw new Error('cart.js ' + r.status);
  return r.json();
}

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => ids.add(el.dataset.sectionId));
  return Array.from(ids).join(',');
}

async function post(path, payload) {
  const sections = sectionIds();
  const body = { ...payload };
  if (sections) {
    body.sections = sections;
    body.sections_url = window.location.pathname;
  }
  const r = await fetch(`${root}cart/${path}.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(body),
  });
  let data = null;
  try { data = await r.json(); } catch (e) { /* ignore */ }
  if (!r.ok || (data && data.status && data.status >= 400)) {
    const err = new Error((data && (data.description || data.message)) || 'cart request failed ' + r.status);
    err.data = data;
    throw err;
  }
  return data;
}

function plan(cart) {
  const mains = cart.items.filter(needsFee);
  const want = new Map(mains.map((m) => [m.key, m.quantity]));
  const groups = new Map();
  cart.items.filter(isFee).forEach((f) => {
    const k = (f.properties && f.properties._fee_for) || '';
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(f);
  });
  const updates = {};
  groups.forEach((list, k) => {
    if (!want.has(k)) { list.forEach((f) => { updates[f.key] = 0; }); return; } // orphan fee -> remove
    list.forEach((f, i) => {
      if (i > 0) updates[f.key] = 0; // duplicate
      else if (f.quantity !== want.get(k)) updates[f.key] = want.get(k);
    });
  });
  const adds = mains
    .filter((m) => !groups.has(m.key))
    .map((m) => ({ id: FEE_VARIANT_ID, quantity: m.quantity, properties: { _fee_for: m.key } }));
  return { updates, adds, mains };
}

function toast(message) {
  const el = document.createElement('div');
  el.setAttribute('role', 'alert');
  el.className = 'lylaloo-fee-toast';
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 9000);
}

function refreshUI(cartJson, sections) {
  try {
    const { promise, resolve } = CartLinesUpdateEvent.createPromise();
    const ev = new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(FEE_VARIANT_ID), quantity: 1 }],
      promise,
    });
    ev[OWN] = true;
    document.dispatchEvent(ev);
    resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson),
      detail: {
        items: cartJson.items,
        itemCount: cartJson.item_count,
        sections: sections || undefined,
        source: 'lylaloo-design-fee',
        didError: false,
      },
    });
  } catch (e) {
    console.warn('[design-fee] UI refresh failed', e);
    if (/^\/cart/.test(window.location.pathname)) window.location.reload();
  }
}

async function reconcileOnce(opts) {
  let cart = await getCart();
  let { updates, adds } = plan(cart);
  if (!Object.keys(updates).length && !adds.length) return;

  let last = null;
  let failed = false;
  if (Object.keys(updates).length) {
    try { last = await post('update', { updates }); } catch (e) { console.warn('[design-fee] update failed', e); failed = true; }
  }
  if (adds.length) {
    for (let attempt = 0; attempt < 2; attempt++) {
      try { last = await post('add', { items: adds }); failed = false; break; } catch (e) {
        console.warn('[design-fee] add failed', e);
        failed = true;
      }
    }
  }

  cart = await getCart();
  // Never leave a photo item without its fee when this change came from the customer adding it.
  const after = plan(cart);
  const unsafe = after.mains.filter((m) => !cart.items.some((f) => isFee(f) && f.properties && f.properties._fee_for === m.key && f.quantity >= m.quantity));
  if (failed && opts.rollback && unsafe.length) {
    for (const m of unsafe) {
      try { last = await post('change', { id: m.key, quantity: 0 }); } catch (e) { console.warn('[design-fee] rollback failed', e); }
    }
    cart = await getCart();
    toast('Sorry, we could not add the custom design fee, so that item was not added. Please try again.');
  }
  refreshUI(cart, last && last.sections);
}

async function run(opts) {
  if (running) { queued = { rollback: (queued && queued.rollback) || opts.rollback }; return; }
  running = true;
  try {
    await reconcileOnce(opts);
    // one more pass catches anything that changed while we were working
    let guard = 0;
    while (queued && guard++ < 3) {
      const o = queued; queued = null;
      await reconcileOnce(o);
    }
  } catch (e) {
    console.warn('[design-fee] reconcile error', e);
  } finally {
    running = false;
  }
}

function schedule(opts) {
  clearTimeout(timer);
  const merged = { rollback: !!opts.rollback };
  timer = setTimeout(() => run(merged), 150);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event[OWN] || !event.promise) return;
  event.promise
    .then(({ detail }) => {
      if (detail && detail.source === 'lylaloo-design-fee') return;
      schedule({ rollback: !!(detail && detail.source === 'product-form-component' && !detail.didError) });
    })
    .catch(() => {});
});

function initialCheck() {
  let count = 0;
  try { count = Number(JSON.parse(sessionStorage.getItem('cart-count') || '{}').value || 0); } catch (e) { /* ignore */ }
  if (count > 0 || /^\/cart/.test(window.location.pathname)) setTimeout(() => schedule({ rollback: false }), 800);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialCheck, { once: true });
else initialCheck();
window.addEventListener('pageshow', (e) => { if (e.persisted) initialCheck(); });
